import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CARRIER_TYPES, CarrierType } from "@shared/schema";

interface Carrier {
  id: number;
  name: string;
  types: CarrierType[];
  description?: string;
  isActive: boolean;
  createdAt: string;
}

interface CarrierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCarrierCreated?: (carrier: Carrier) => void;
}

export function CarrierDialog({
  open,
  onOpenChange,
  onCarrierCreated,
}: CarrierDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [types, setTypes] = useState<CarrierType[]>([CarrierType.HEALTH]);
  const [submitting, setSubmitting] = useState(false);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);

  // Fetch carriers on open
  useEffect(() => {
    if (open) {
      fetchCarriers();
    }
  }, [open]);

  const fetchCarriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/carriers");
      if (!res.ok) throw new Error("Failed to fetch carriers");
      const data = await res.json();
      setCarriers(data);
    } catch (e: any) {
      setError(e.message || "Error loading carriers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (types.length === 0) {
      setError("Please select at least one carrier type");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let carrier: Carrier;
      if (editId) {
        const res = await fetch(`/api/carriers/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, description, types }),
        });
        if (!res.ok) throw new Error("Failed to update carrier");
        carrier = await res.json();
      } else {
        const res = await fetch("/api/carriers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, description, types }),
        });
        if (!res.ok) throw new Error("Failed to create carrier");
        carrier = await res.json();
      }
      setName("");
      setDescription("");
      setTypes([CarrierType.HEALTH]);
      setEditId(null);
      if (onCarrierCreated) onCarrierCreated(carrier);
      fetchCarriers();
      onOpenChange(false);
    } catch (e: any) {
      setError(e.message || "Error saving carrier");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (carrier: Carrier) => {
    setEditId(carrier.id);
    setName(carrier.name);
    setDescription(carrier.description || "");
    setTypes(carrier.types && carrier.types.length > 0 ? carrier.types : [CarrierType.HEALTH]);
  };

  const toggleType = (carrierType: CarrierType) => {
    setTypes((prev) =>
      prev.includes(carrierType)
        ? prev.filter((t) => t !== carrierType)
        : [...prev, carrierType]
    );
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this carrier?")) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/carriers/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete carrier");
      setCarriers(carriers.filter((c) => c.id !== id));
    } catch (e: any) {
      setError(e.message || "Error deleting carrier");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setName("");
    setDescription("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editId ? "Edit Carrier" : "Add New Carrier"}
          </DialogTitle>
          <DialogDescription>
            {editId
              ? "Update the carrier details."
              : "Create a new insurance carrier to use in your plans."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Carrier Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Anthem Blue Cross"
              required
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Carrier Types (select at least one)
            </label>
            <div className="space-y-2 border rounded-md p-3">
              {CARRIER_TYPES.map((carrierType) => (
                <div key={carrierType} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${carrierType}`}
                    checked={types.includes(carrierType)}
                    onCheckedChange={() => toggleType(carrierType)}
                    disabled={submitting}
                  />
                  <label
                    htmlFor={`type-${carrierType}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize cursor-pointer"
                  >
                    {carrierType.replace(/_/g, " ")}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Description (Optional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the carrier or add notes..."
              className="min-h-20"
              disabled={submitting}
            />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            {editId && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting
                ? editId
                  ? "Saving..."
                  : "Creating..."
                : editId
                ? "Save"
                : "Create Carrier"}
            </Button>
          </div>
        </form>
        <hr className="my-4" />
        <div>
          <h4 className="font-medium mb-2">Existing Carriers</h4>
          {loading ? (
            <div>Loading...</div>
          ) : carriers.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              No carriers found.
            </div>
          ) : (
            <ul className="space-y-2 max-h-40 overflow-y-auto">
              {carriers.map((carrier) => (
                <li
                  key={carrier.id}
                  className="flex items-center justify-between gap-2 border rounded px-2 py-1"
                >
                  <div>
                    <div className="font-medium">{carrier.name}</div>
                    {carrier.types && carrier.types.length > 0 && (
                      <div className="text-xs text-muted-foreground capitalize">
                        {carrier.types.join(", ")}
                      </div>
                    )}
                    {carrier.description && (
                      <div className="text-xs text-muted-foreground">
                        {carrier.description}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(carrier)}
                      disabled={submitting}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(carrier.id)}
                      disabled={submitting}
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
